import * as Sentry from "@sentry/react";
import { supabase } from "@common/lib/supabaseClient";

/**
 * Sign out the current device only. Use this for ALL sign-out flows in the app.
 *
 * Supabase's `signOut()` defaults to `global` scope, which revokes refresh tokens
 * on every device the user is signed in on. For HappyDoc's multi-device exam-room
 * workflow that's almost never what we want — locking a screen on one tablet
 * shouldn't kick the vet out of every other tablet too.
 *
 * Errors are reported to Sentry with `errorCategory: "auth"` so callers can safely
 * fire-and-forget (`void signOutLocal()`) without dropping failures on the floor.
 * A thrown (not returned) signOut — e.g. a network failure — is caught too, so this
 * NEVER rejects: callers that chain `.finally(nav)` (AccountProvider, PermissionWrapper's
 * banned-user branch) always navigate. (retryWrapper does NOT use this — it inlines its own
 * local signOut inside its retry try/catch, navigating unconditionally right after, to avoid a
 * circular dependency; see retryWrapper.ts.)
 *
 * If you genuinely need to sign out everywhere (account deletion, global security
 * event), call `supabase.auth.signOut({ scope: "global" })` directly with a comment
 * explaining why.
 */
export async function signOutLocal(): Promise<{ error: unknown }> {
    try {
        const result = await supabase.auth.signOut({ scope: "local" });
        if (result.error) {
            Sentry.captureException(result.error, {
                tags: { errorCategory: "auth", operation: "signOutLocal" },
            });
        }
        return result;
    } catch (error) {
        Sentry.captureException(error, {
            tags: { errorCategory: "auth", operation: "signOutLocal" },
        });
        return { error };
    }
}
